import moment from 'moment';
import React, { useState, useRef, useMemo } from 'react';
import { actionsTypes } from '../../../../config';
import { createRoot } from 'react-dom/client';
import OrderDetail from '../../components/OrderDetail';
import { useDispatch } from 'react-redux';
import { setTradeDialog, setTradeElementKey } from '../../../../redux/reducer/dialog';
import useChangeSymbol from '../../../../hooks/useChangeSymbol';

function OrderVirtualList({
  data,
  itemHeight = 50,
  containerHeight = 900,
  overscan = 3 // จำนวนรายการเพิ่มเติม ด้านบนล่าง
}) {
  const [scrollTop, setScrollTop] = useState(0);
  const containerRef = useRef(null);

  // คำนวณความสูงทั้งหมด
  const totalHeight = data.length * itemHeight;

  // ใช้ความสูงที่เหมาะสม (ไม่เกิน totalHeight)
  const actualContainerHeight = Math.min(containerHeight, totalHeight);

  // คำนวณ visible range พร้อม overscan
  const { startIndex, endIndex, offsetY } = useMemo(() => {
    const visibleStartIndex = Math.floor(scrollTop / itemHeight);
    const visibleEndIndex = Math.ceil((scrollTop + actualContainerHeight) / itemHeight);

    // เพิ่ม buffer ด้านบนล่าง
    const start = Math.max(0, visibleStartIndex - overscan);
    const end = Math.min(data.length, visibleEndIndex + overscan);
    const offset = start * itemHeight;

    return {
      startIndex: start,
      endIndex: end,
      offsetY: offset
    };
  }, [scrollTop, itemHeight, actualContainerHeight, data.length, overscan]);

  const visibleItems = useMemo(
    () => data.slice(startIndex, endIndex),
    [data, startIndex, endIndex]
  );

  const handleScroll = (e) => {
    setScrollTop(e.target.scrollTop);
  };

  return (
    <div
      ref={containerRef}
      id="custom-order-scroll"
      onScroll={handleScroll}
      style={{
        height: 'calc(100% - 310px)',
        overflow: totalHeight > actualContainerHeight ? 'auto' : 'hidden',
        position: 'relative'
      }}>
      <div
        style={{
          position: 'relative'
        }}>
        <div
          // id="custom-order-scroll"
          className="table-order-scroll-order history"
          style={{
            transform: `translateY(${offsetY}px)`,
            willChange: 'transform'
          }}>
          {visibleItems.map((item, index) => (
            <VirtualListItem
              key={startIndex + index}
              item={item}
              index={startIndex + index}
              height={itemHeight}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

const renderAction = (x) => {
  switch (x) {
    default:
      return (
        <div style={{ color: actionsTypes[x]?.color }} className="font-bold text-sm">
          {actionsTypes[x]?.name}
        </div>
      );
  }
};

// แยก Component เพื่อ optimize การ render
export const VirtualListItem = React.memo(({ item, index, height }) => {
  const dispatch = useDispatch();
  const handleClose = () => {
    dispatch(setTradeDialog({ isOpen: false, type: '' }));
  };

  const renderOrderDetail = (e, handleClose, setElementKey, changeSymbol) => {
    const panel = document.getElementById('history-panel');
    if (panel) {
      const el = document.createElement('div');
      el.style.position = 'absolute';
      el.style.top = '0';
      el.style.left = '0';
      el.style.width = '100%';
      el.style.height = '100%';
      el.style.zIndex = '99';
      el.id = 'OrderDetail';

      const handleBack = () => {
        panel.removeChild(el);
      };

      panel.appendChild(el);
      setElementKey('OrderDetail');
      createRoot(el).render(
        <OrderDetail
          onBack={handleBack}
          data={e}
          handleClose={handleClose}
          changeSymbol={changeSymbol}
        />
      );
      return el;
    }
    return null;
  };

  const setElementKey = (key) => {
    dispatch(setTradeElementKey(key));
  };

  const changeSymbol = useChangeSymbol(dispatch);

  return (
    <div
      key={index}
      onClick={() => renderOrderDetail(item, handleClose, setElementKey, changeSymbol)}
      className="table-body hover:bg-[#404040] h-[auto]"
      style={{
        padding: '8px',
        margin: '0px 16px',
        borderRadius: '8px'
        // opacity: type === 'history' ? 1 : 0/
      }}>
      <div className="flex h-full items-center">
        <div className="flex w-full">
          <div className="w-[32px] content-center">
            <img
              src={`https://storage.googleapis.com/iux-storage/symbol/${item.symbol}.png`}
              alt="..."
              onError={({ currentTarget }) => {
                currentTarget.src = 'globe.svg';
              }}
              className="w-full rounded-[50%]"
            />
          </div>
          <div className="ml-3 flex flex-col gap-1">
            <div className="flex items-baseline text-sm">
              <div className="text-[#e5e5e5] font-bold">{item.symbol}</div>
              <span className="ml-1">{renderAction(item.type)}</span>
            </div>
            <div className="flex text-[#e5e5e5]">
              <div style={{ color: '#9f9f9f' }} className="flex items-center">
                <div className="text-xs">{item.volumeInitial / 10000}</div>
                {item.priceOrder === 0 ? (
                  <span className={`text-xs mx-1 text-[#9f9f9f]`}>at market</span>
                ) : item.priceOrder ? (
                  <>
                    <span className={`text-xs mx-1`}>at</span>
                    <div className={`text-xs`}>{item.priceOrder?.toFixed(item.digits)}</div>
                  </>
                ) : (
                  <div className="text-xs">{item.comment}</div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-1 text-sm">
          <div
            className="font-bold"
            style={{
              color:
                item?.state === 4
                  ? '#24A759'
                  : item?.state === 2
                  ? '#F3C006'
                  : item?.state === 6
                  ? '#9F9F9F'
                  : item?.state === 5
                  ? '#EF4533'
                  : '',
              textAlign: 'right'
            }}>
            {item?.state === 4
              ? 'Filled'
              : item?.state === 2
              ? 'Canceled'
              : item?.state === 6
              ? 'Expired'
              : item?.state === 5
              ? 'Rejected'
              : ''}
          </div>
          <div className="text-[#9f9f9f] text-xs whitespace-pre">
            {moment.unix(item.timeDone).utc().format('YYYY.MM.DD HH:mm:ss')}
          </div>
        </div>
      </div>
    </div>
  );
});

VirtualListItem.displayName = 'VirtualListItem';

export default OrderVirtualList;
